import { Box, TextField } from '@mui/material';
import { ArrowDownIcon } from '~/assets/icons';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './BaseTimePicker.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

export type BaseTimePickerProps = {
  value?: Date;
  placeholder?: string;
  onChange?: (date: Date | null) => void;
  error?: boolean;
  helperText?: string;
  customInput?: JSX.Element;
};

export const BaseTimePicker = (props: BaseTimePickerProps) => {
  //#region Destructuring Props
  const { value, placeholder, error, helperText } = props;
  //#endregion Destructuring Props

  //#region Declare State
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [timeText, setTimeText] = useState<string>('');
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (value) {
      setSelectedTime(value);
      setTimeText(dayjs(value).format('h:mm A'));
    }
  }, [value]);
  //#endregion Implement Hook

  //#region Handle Function
  const onChange = (selectedTime: Date | null) => {
    if (props.onChange) props.onChange(selectedTime);
    if (selectedTime) {
      setSelectedTime(selectedTime);
      setTimeText(dayjs(selectedTime).format('hh:mm A'));
    }
  };

  //#endregion Handle Function

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(({ onClick }, ref) => (
    <TextField
      ref={ref}
      inputProps={{ readOnly: true }}
      InputProps={{
        endAdornment: <ArrowDownIcon className='w-[13px]' />,
      }}
      onClick={onClick}
      value={timeText}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
    />
  ));

  return (
    <Box className={cx('base-time-picker-container')}>
      <Box className={cx('base-time-picker-menu')}>
        <DatePicker
          selected={selectedTime}
          onChange={onChange}
          showTimeSelect
          showTimeSelectOnly
          showPopperArrow={false}
          timeIntervals={30}
          customInput={props?.customInput || <TextFieldInput readOnly />}
          popperPlacement='top-start'
          dateFormat='HH:mm'
        />
      </Box>
    </Box>
  );
};

export default memo(BaseTimePicker);
